import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { CounterView } from './ProductCardParent'
import { SideContainer } from './ProductCard'
import { CartItem } from '../../../cart/storage/cart.types'
import { useAppDispatch } from '../../../../store/rootStore'
import CartSlice, { selectCurrentCart } from '../../../cart/storage/cart.slice'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import Counter from '../../../../components/Counter'
import { Variant } from '../../utils/useVariations'
import { getUrgentDeliveryUnavailable } from '../../../cart/utils/getUrgentDeliveryUnavailable'
import { CartItemCard } from './CartItemCard'

export interface CartProductCardProps {
  className?: string
  item: CartItem
  onChangeQuantity?: (value: number) => void
  onChangeVariant?: (value: Variant) => void
  enableQuantity?: boolean
  enableVariants?: boolean
  enableRemove?: boolean
  handleOpenEditPriceModal?: () => void
  priceEditable?: boolean
}

export const CartProductCard: React.FC<CartProductCardProps> = ({
  className,
  item,
  onChangeQuantity,
  onChangeVariant,
  enableQuantity = true,
  enableVariants,
  enableRemove = true,
  handleOpenEditPriceModal,
  priceEditable = false,
}) => {
  const dispatch = useAppDispatch()
  const currentAccount = useSelector(selectCurrentFarmerAccount)

  const cart = useSelector(selectCurrentCart)

  const showUrgentDeliveryUnavailableMessage = React.useMemo(() => {
    if (!currentAccount || !cart) return false
    return getUrgentDeliveryUnavailable({
      cart,
      productId: item.productId,
      stockAvailabilities: item.stockAvailabilities,
      homeBranchId: currentAccount?.homeBranchId,
    })
  }, [item, cart, currentAccount?.homeBranchId])

  function onRemove() {
    if (currentAccount) {
      dispatch(
        CartSlice.actions.removeItem({
          accountNumber: currentAccount.accountNumber,
          product: item,
        }),
      )
      toast('Item Removed', {
        type: 'success',
        icon: <MaterialSymbol>check_circle</MaterialSymbol>,
      })
    }
  }

  const renderSideContainer = useMemo(
    () =>
      enableQuantity ? (
        <SideContainer>
          <CounterView>
            <Counter
              value={item.quantity}
              onChange={(value) => {
                if (onChangeQuantity) {
                  onChangeQuantity(value)
                }
              }}
            />
          </CounterView>
        </SideContainer>
      ) : null,
    [item.quantity, onChangeQuantity, enableQuantity],
  )

  return (
    <CartItemCard
      className={className}
      item={item}
      onRemove={enableRemove ? onRemove : undefined}
      onChangeVariant={onChangeVariant}
      enableVariants={enableVariants}
      handleOpenEditPriceModal={handleOpenEditPriceModal}
      priceEditable={priceEditable}
      renderSideContainer={renderSideContainer}
      showUrgentDeliveryUnavailableMessage={showUrgentDeliveryUnavailableMessage}
    />
  )
}
